import React from 'react'
import { View } from 'react-native'
import { ModalBottom } from '@ui/components/Modal'
import Button from '@ui/buttons/Button'
import { useTranslation } from 'react-i18next'
import { useCountries } from 'hooks/options/useCountries'
import { FormProvider } from 'react-hook-form'
import FormInput from '@ui/forms/Input/FormInput'
import FormSelect from '@ui/forms/Select/FormSelect'
import useInvoiceForm from './useInvoiceForm'
import { markDetailSectionSaved } from '@user/customer/confirmation/markDetailSectionSaved'
import FormInputDate from '@ui/forms/Date/FormInputDate'
import { BirthdateValues } from '@ca/common'

interface Props {
  open: boolean
  close: () => void
}

export default function InvoiceModal({ open, close }: Props) {
  const { t } = useTranslation(['ui', 'modals', 'user'])
  const countries = useCountries()
  const { form, create, patch, onClose, onSubmit } = useInvoiceForm({
    open,
    close,
    onSuccess: () => markDetailSectionSaved('invoice'),
  })

  return (
    <ModalBottom
      open={open}
      close={close}
      title={t('modals:invoice.title')}
      desc={t('modals:invoice.desc')}
      body={
        <FormProvider {...form}>
          <View className="gap-6 px-16">
            <FormInput
              control={form.control}
              label={t('user:firstName')}
              name="firstName"
              next="lastName"
              autoCapitalize="words"
            />
            <FormInput
              control={form.control}
              label={t('user:lastName')}
              name="lastName"
              next="vatId"
              autoCapitalize="words"
            />
            <FormInputDate
              control={form.control}
              label={t('user:birthDate')}
              name="birth_date"
              min={BirthdateValues.min}
              max={BirthdateValues.max}
              defaultDate={BirthdateValues.default}
            />
            <FormInput
              control={form.control}
              label={t('user:vat')}
              name="vatId"
              next="email"
            />
            <FormInput
              control={form.control}
              label={t('user:email')}
              name="email"
              next="mobile"
            />
            <FormInput
              control={form.control}
              label={t('user:mobile')}
              name="mobile"
              next="street"
            />
            <FormInput
              control={form.control}
              label={t('user:street')}
              name="street"
              next="street2"
            />
            <FormInput
              control={form.control}
              label={t('user:street2')}
              name="street2"
              next="city"
            />
            <FormInput
              control={form.control}
              label={t('user:city')}
              name="city"
              next="zip"
            />
            <FormInput
              control={form.control}
              label={t('user:zip')}
              name="zip"
            />
            <FormSelect
              control={form.control}
              label={t('user:country')}
              name="country_code"
              options={countries}
              placeholder={t('user:address.country')}
            />
          </View>
        </FormProvider>
      }
      buttons={
        <>
          <Button variant="outlined" label={t('ui:close')} onPress={onClose} />
          <Button
            variant="filled"
            label={t('ui:save')}
            iconEnd="arrow-narrow-right"
            loading={patch.isPending || create.isPending}
            onPress={onSubmit}
          />
        </>
      }
    />
  )
}
